import React, { useState } from 'react';
import { toast } from 'sonner';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import frLabels from 'react-phone-number-input/locale/fr';
import enLabels from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { useTranslations } from '../translations/translations';
import { useLanguage } from '../contexts/LanguageContext';
import { trackLead } from '../analytics/tracking';
import MissionsCarousel from './MissionsCarousel';

const ContactMinimal = React.forwardRef((props, ref) => {
  const t = useTranslations();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPossiblePhoneNumber(formData.phone)) {
      toast.error(t.contact.form.phoneInvalid);
      return;
    }
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t.contact.form.success);
        trackLead();
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(data.detail || t.contact.form.error);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} id="contact" style={{ background: '#000', padding: '10rem 0 5rem' }}>
      <div className="container-custom">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Notice d'excellence - AVANT le formulaire */}
          <div style={{ 
            marginBottom: '4rem',
            padding: '2rem',
            border: '1px solid #262626',
            borderRadius: '8px',
            background: 'rgba(10, 10, 10, 0.8)',
            textAlign: 'center'
          }}>
            <p style={{ 
              fontSize: '1rem', 
              color: '#a3a3a3',
              lineHeight: 1.6,
              fontWeight: 300,
              letterSpacing: '0.3px',
              fontStyle: 'italic'
            }}>
              {t.excellence.text}
            </p>
          </div>

          <div className="section-header">
            <h2 className="section-title">{t.contact.title}</h2>
            <p className="section-subtitle">
              {t.contact.subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ marginTop: '4rem' }}>
            <div style={{ display: 'grid', gap: '2.5rem' }}>
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contact.form.name}
                  required
                  style={{
                    width: '100%',
                    padding: '1.25rem 0',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #404040',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 300,
                    transition: 'border-color 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                  onBlur={(e) => e.target.style.borderBottomColor = '#404040'}
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contact.form.email}
                  required
                  style={{
                    width: '100%',
                    padding: '1.25rem 0',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #404040',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 300,
                    transition: 'border-color 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                  onBlur={(e) => e.target.style.borderBottomColor = '#404040'}
                />
              </div>

              <div>
                <PhoneInput
                  className="contact-phone"
                  international
                  defaultCountry="FR"
                  countryCallingCodeEditable={false}
                  labels={language === 'en' ? enLabels : frLabels}
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder={t.contact.form.phone}
                  required
                />
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.form.message}
                  required
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '1.25rem 0',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #404040',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 300,
                    transition: 'border-color 0.3s ease',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                  onBlur={(e) => e.target.style.borderBottomColor = '#404040'}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  marginTop: '2rem',
                  padding: '1.25rem 4rem',
                  background: 'transparent',
                  border: '1px solid #ffffff',
                  color: '#ffffff',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.5s ease',
                  alignSelf: 'flex-start'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#ffffff';
                  e.target.style.color = '#000000';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#ffffff';
                }}
              >
                {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
              </button>
            </div>
          </form>

          {/* Carousel "Nos Missions" sous le formulaire */}
          <MissionsCarousel />
        </div>
      </div>
    </section>
  );
});

ContactMinimal.displayName = 'ContactMinimal';

export default ContactMinimal;
